import find from 'lodash/find'
import forEach from 'lodash/forEach'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import { getAccountIdentifier } from 'v2/pages/settings/integrations/utils/account-identifier'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import dayjs from 'v2/utils/dayjs'
import { errorToast } from 'v2/utils/toasts'
import { ActionConnectionLabel } from './action-connection-label'
import { getActionConnectionAvailability, matchesActionProvider } from './integration-action-source'

interface ActionOption {
  label: React.ReactNode
  value: string
  [key: string]: unknown
}

export const applyActionConnectionAvailability = <T extends ActionOption>(
  app: RExternalOrgApp,
  options: T[],
  aciEnabled = false,
): T[] => {
  const availability = getActionConnectionAvailability(app, aciEnabled)
  return map(options, (option) => ({
    ...option,
    disabled: availability.disabled,
    tooltipContent: availability.tooltipContent,
  }))
}

export const guardActionConnectionSelection = (app: RExternalOrgApp, aciEnabled = false) => {
  const availability = getActionConnectionAvailability(app, aciEnabled)
  if (!availability.disabled) return true
  errorToast({
    title: availability.migrationLabel || 'Connection unavailable',
    description: availability.tooltipContent,
  })
  return false
}

export const findSelectableActionConnection = (
  apps: RExternalOrgApp[] | undefined,
  predicate: (app: RExternalOrgApp) => boolean,
  aciEnabled = false,
) =>
  find(apps, (app) => predicate(app) && !getActionConnectionAvailability(app, aciEnabled).disabled)

/**
 * The single way to bind a saved legacy action — one that carries `app_provider`
 * but no `org_app_uuid` — to a live connection.
 *
 * Both action pickers (the AI-agent one and the AI-action node one) must resolve
 * it identically: matching raw provider on one surface and aliasing through
 * `providerKey` on the other made the same saved action auto-bind in one place
 * and silently stay unbound in the other.
 */
export const findActionConnectionForSavedProvider = (
  apps: RExternalOrgApp[] | undefined,
  savedProvider: string,
  aciEnabled = false,
) =>
  findSelectableActionConnection(
    apps,
    (app) => matchesActionProvider(app.provider, savedProvider, aciEnabled),
    aciEnabled,
  )

/**
 * Connections as flat select options — the "pick a connection first" step.
 *
 * Legacy rows stay visible but unselectable so their actions cannot be saved into
 * new agent actions; `searchValue` carries the text because the label is a node.
 */
export const buildConnectionOptions = (
  apps: RExternalOrgApp[],
  accountLabels: Record<string, string>,
  aciEnabled = false,
) =>
  map(apps, (app) => {
    const availability = getActionConnectionAvailability(app, aciEnabled)
    const accountInfo = accountLabels[app.id]
    return {
      value: app.id,
      label: <ActionConnectionLabel app={app} accountInfo={accountInfo} aciEnabled={aciEnabled} />,
      searchValue: `${app.name} ${accountInfo ?? ''}`.trim(),
      icon: app.logo_url ? (
        <img src={app.logo_url} alt={app.name} className="size-4 rounded" />
      ) : undefined,
      disabled: availability.disabled,
      tooltipContent: availability.tooltipContent,
    }
  })

/** The actions of one connection, keyed by the same encoded identifier the grouped list uses. */
export const buildActionOptionsForConnection = (
  app: RExternalOrgApp | undefined,
  mcpActions: { name: string; description: string }[] = [],
  encodeValue: (actionName: string, appId: string) => string,
) => {
  if (!app) return []
  return [
    ...map(app.actions ?? [], (action) => ({
      label: action.description,
      value: encodeValue(action.action_name, app.id),
      searchValue: `${action.action_name} ${action.description}`,
    })),
    ...map(mcpActions, (action) => ({
      label: action.description,
      value: encodeValue(action.name, app.id),
      searchValue: `${action.name} ${action.description}`,
    })),
  ]
}

export const buildActionConnectionAccountLabels = (
  apps: RExternalOrgApp[],
  userMap: Record<string, string>,
) => {
  const baseLabels = new Map(
    map(apps, (app): [string, string] => {
      const enrichedApp =
        app.integrated_by?.id && userMap[app.integrated_by.id]
          ? { ...app, integrated_by: { ...app.integrated_by, name: userMap[app.integrated_by.id] } }
          : app
      return [app.id, getAccountIdentifier(enrichedApp)]
    }),
  )
  const counts = new Map<string, number>()
  forEach(apps, (app) => {
    const key = `${app.name}|${baseLabels.get(app.id)}`
    counts.set(key, (counts.get(key) ?? 0) + 1)
  })

  return fromPairs(
    map(apps, (app) => {
      let label = baseLabels.get(app.id) ?? ''
      if ((!label || (counts.get(`${app.name}|${label}`) ?? 0) > 1) && app.created_on) {
        const created = dayjs(app.created_on).format('MMM D, hh:mm A')
        label = label ? `${label} · ${created}` : created
      }
      return [app.id, label]
    }),
  )
}
